import { FC, useEffect, useState } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { Datatable } from '../../../components/Datatable/Datatable'
import type { MetaType } from '../../../components/Datatable/Datatable'
import { Button } from '../../../components/Button/Button'
import useModals from '../../../hooks/useModals'
import { DELETE, GET, PUT } from '../../../services/api'
import { Loading } from '../../../components/Loading/Loading'
import { ModalDelete } from '../../../components/Modals/General/ModalDelete/ModalDelete'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { i18n } from '../../../translate/i18n'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import {
  HotelTourType,
  getDifficultyLabel,
  getDifficultyColor,
  formatCurrency,
  formatRating,
  formatSchedules,
  formatIncludes
} from './types'

const HotelTourContent: FC = () => {
  const navigate = useNavigate()
  const { showDeleteModal } = useModals()
  const [loading, setLoading] = useState(false)
  const [tours, setTours] = useState<HotelTourType[]>([])
  const [id, setId] = useState<number | null>(null)
  const [meta, setMeta] = useState<MetaType | undefined>(undefined)

  function showPrice(value: string) {
    if (value && parseFloat(value) > 0) {
      return `R$ ${parseFloat(value).toFixed(2)}`
    }
    return i18n.t("general.paymentFree")
  }
  const getTours = async () => {
    try {
      setLoading(true)
      const res = await GET('/hotel-tours')
      const formattedTours = res.data.map((tour: any) => ({
        id: tour.id,
        title: tour.title,
        description: tour.description,
        duration: tour.duration,
        difficulty: tour.difficulty,
        price: showPrice(tour.price),
        currency: tour.currency,
        rating: tour.rating ? parseFloat(tour.rating) : 0,
        image: tour.image,
        status: tour.status,
        requiresReservation: tour.requiresReservation,
        maxParticipants: tour.maxParticipants,
        minParticipants: tour.minParticipants,
        ageRestriction: tour.ageRestriction,
        physicalRequirements: tour.physicalRequirements,
        weatherDependent: tour.weatherDependent,
        cancellationHours: tour.cancellationHours,
        discountChildren: tour.discountChildren ? parseFloat(tour.discountChildren) : 0,
        includesTransfer: tour.includesTransfer,
        pluginspaceId: tour.pluginspaceId,
        created: tour.created,
        updated: tour.updated,
        tourIncludes: tour.tourIncludes || [],
        tourSchedules: tour.tourSchedules || [],
        difficultyLabel: i18n.t(`hotel_tour.difficult.${tour.difficulty}`),// getDifficultyLabel(tour.difficulty),
        priceFormatted: formatCurrency(tour.price, tour.currency),
        ratingFormatted: formatRating(tour.rating),
        reservationLabel: tour.requiresReservation ? 'Sim' : 'Não',
        statusLabel: tour.status ? 'Ativo' : 'Inativo',
        transferLabel: tour.includesTransfer ? 'Sim' : 'Não',
        weatherLabel: tour.weatherDependent ? 'Sim' : 'Não',
        shortDescription: tour.description && tour.description.length > 50
          ? tour.description.substring(0, 50) + '...'
          : tour.description || '-',
        participantsRange: tour.maxParticipants
          ? `${tour.minParticipants}-${tour.maxParticipants}`
          : `${tour.minParticipants}+`,
        schedulesFormatted: formatSchedules(tour.tourSchedules || []),
        includesFormatted: formatIncludes(tour.tourIncludes || []),
        schedulesCount: tour.tourSchedules ? tour.tourSchedules.length : 0,
        includesCount: tour.tourIncludes ? tour.tourIncludes.length : 0,
      }))
      setTours(formattedTours)
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const deleteTour = async () => {
    try {
      setLoading(true)
      const response = await DELETE(`/hotel-tours/${id}`)
      if (response.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hotel_tour.msg_success_delete')),
          text: String(i18n.t('hotel_tour.msg_success_delete_text')),
        }).then(async () => await getTours())
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('hotel_tour.msg_error_delete')),
          text: String(i18n.t('hotel_tour.msg_error_delete_text')),
        }).then(async () => await getTours())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hotel_tour.msg_error_delete')),
        text: String(i18n.t('hotel_tour.msg_error_delete_text')),
      }).then(async () => await getTours())
      console.log(e)
    }
  }

  useEffect(() => {
    getTours()
  }, [])

  const switchStatus = async (tour: HotelTourType, status: boolean) => {
    try {
      const switchTour = await PUT(`/hotel-tours/${tour.id}`, {
        status: !status
      })
      if (switchTour.success) {
        const newTours = tours.map((tourItem) => {
          if (tourItem.id === tour.id) {
            tourItem.status = !status
          }
          return tourItem
        })
        setTours(newTours)
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hotel_tour.msg_success_status')),
          text: String(i18n.t('hotel_tour.msg_success_status_text')),
        })
      }
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hotel_tour.msg_error_status')),
        text: String(i18n.t('hotel_tour.msg_error_status_text')),
      })
      console.log(e)
    }
  }




  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hotel_tour.list_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hotel_tour.list_subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('hotel_tour.btn_create')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('criar')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={tours}
                headers={[
                  { key: i18n.t('hotel_tour.field_title'), value: 'title', type: 'default' },
                  { key: i18n.t('hotel_tour.field_difficulty'), value: 'difficultyLabel', type: 'default' },
                  { key: i18n.t('hotel_tour.field_price'), value: 'price', type: 'default' },
                  { key: i18n.t('hotel_tour.field_schedules'), value: 'schedulesFormatted', type: 'default' },
                  { key: i18n.t('hotel_tour.field_includes'), value: 'includesFormatted', type: 'default' },
                  {
                    key: i18n.t('general.active'),
                    value: 'status',
                    type: 'check',
                    action: (item) => switchStatus(item, item.status),
                  },
                  { key: i18n.t('general.actions'), value: 'acoes', type: '' },
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => {
                      navigate(`editar/${item.id}`)
                    },
                    title: i18n.t('hotel_tour.btn_edit'),
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(i18n.t('hotel_tour.btn_delete'))
                      setId(item.id)
                    },
                    title: i18n.t('hotel_tour.btn_delete'),
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          <ModalDelete confirmAction={() => deleteTour()} />
        </div>
      )}
    </>
  )
}

const HotelTour: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: i18n.t('general.dashboard'), path: 'dashboard', isActive: true },
          { title: i18n.t('hotel_tour.list_title'), path: 'hotel-tours', isActive: true },
        ]}
      >
        {i18n.t('hotel_tour.list_title')}
      </PageTitle>
      <HotelTourContent />
    </>
  )
}

export { HotelTour }